/**
 * Per-product / per-skin extra photo library.
 *
 * Admins can attach any number of extra photos to a product handle and
 * (optionally) a specific skin finish name. The product page shows a
 * "more photos" button on the hero image that opens these in a lightbox.
 */
export interface SkinPhotoEntry {
  /** Shopify product handle, e.g. "nintendo-switch-console-skin" */
  handle: string;
  /** Skin finish name, e.g. "Black Matte". Empty = applies to the whole product. */
  skin: string;
  /** Image URLs */
  urls: string[];
}

export interface SkinPhotoLibrary {
  entries: SkinPhotoEntry[];
}

export const DEFAULT_SKIN_PHOTOS: SkinPhotoLibrary = { entries: [] };

const norm = (v?: string | null) => (v || "").trim().toLowerCase();

/**
 * Photos for a handle + finish: finish-specific first, then product-wide.
 */
export function getSkinPhotos(
  library: SkinPhotoLibrary | undefined | null,
  handle?: string | null,
  skin?: string | null,
): string[] {
  const entries = library?.entries;
  if (!entries?.length || !handle) return [];
  const h = norm(handle);
  const s = norm(skin);
  const specific: string[] = [];
  const generic: string[] = [];
  for (const e of entries) {
    if (norm(e.handle) !== h) continue;
    const urls = (e.urls || []).map((u) => u.trim()).filter(Boolean);
    if (!urls.length) continue;
    if (!norm(e.skin)) generic.push(...urls);
    else if (s && norm(e.skin) === s) specific.push(...urls);
  }
  return [...new Set([...specific, ...generic])];
}
